/**
 * Typed client for the camera settings / service endpoints
 * (/api/camera/<device_id>/...). These endpoints talk to the camera over
 * ThingsBoard two-way RPC, so calls take 1-5 s and the photo capture up to
 * the requested timeout. Hand-written on top of the shared axios instance
 * (the committed OpenAPI schema does not cover them yet).
 */
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axiosInstance from "@/api/axios-instance";
import { translate } from "@/i18n/translate";

export interface Roi {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ExposureState {
  ae_mode: "auto" | "manual";
  analogue_gain: number | null;
  exposure_time: number | null;
  last_auto_analogue_gain?: number | null;
  last_auto_exposure_time?: number | null;
}

export interface WhiteBalanceState {
  awb_mode: "auto" | "manual";
  awb_red_gain: number | null;
  awb_blue_gain: number | null;
  last_auto_colour_gains?: [number, number] | null;
}

export interface FocusState {
  af_mode: number; // 0 = manual, 1 = auto, 2 = continuous
  af_state: number | null;
  af_state_name: string;
  lens_position: number | null;
  roi: Roi | null;
  roi_active: boolean;
  resolution?: [number, number] | string;
}

export interface AutofocusResult {
  converged: boolean;
  af_state: number | null;
  af_state_name: string | null;
  lens_position: number | null;
  duration_ms: number | null;
  locked: boolean;
}

/** Camera stored-settings block echoed back by PUT responses. */
export interface AppliedSettings {
  ae_enable?: boolean;
  analogue_gain?: number;
  exposure_time?: number;
  awb_enable?: boolean;
  awb_red_gain?: number;
  awb_blue_gain?: number;
  focus_settings?: {
    af_mode?: number;
    lens_position?: number;
    roi?: Roi | null;
  };
}

export interface SetFocusResponse {
  success: boolean;
  error?: string;
  mode: string;
  focus: FocusState;
  applied_settings?: AppliedSettings;
  autofocus?: AutofocusResult;
}

export interface StreamStartResponse {
  success: boolean;
  endpoint: string;
  stream_device_name?: string;
  quality: number;
  view_url?: string;
}

const controlKey = (deviceId: string, part: string) => [
  "camera-control",
  deviceId,
  part,
];

export const backendErrorMessage = (error: unknown): string => {
  const maybeAxios = error as {
    response?: { data?: { error?: string } };
    message?: string;
  };
  return (
    maybeAxios?.response?.data?.error ??
    maybeAxios?.message ??
    translate("cameras.control.unknownError")
  );
};

// ---------------------------------------------------------------------------
// Live state queries (each one is a ThingsBoard RPC to the camera)
// ---------------------------------------------------------------------------

const liveQueryOptions = {
  refetchOnWindowFocus: false,
  retry: 0,
  staleTime: 10_000,
};

export const useCameraExposure = (deviceId: string | null) =>
  useQuery({
    queryKey: controlKey(deviceId ?? "", "exposure"),
    queryFn: async () => {
      const { data } = await axiosInstance.get<{ exposure: ExposureState }>(
        `/api/camera/${deviceId}/exposure`,
      );
      return data.exposure;
    },
    enabled: !!deviceId,
    ...liveQueryOptions,
  });

export const useCameraWhiteBalance = (deviceId: string | null) =>
  useQuery({
    queryKey: controlKey(deviceId ?? "", "white_balance"),
    queryFn: async () => {
      const { data } = await axiosInstance.get<{
        white_balance: WhiteBalanceState;
      }>(`/api/camera/${deviceId}/white_balance`);
      return data.white_balance;
    },
    enabled: !!deviceId,
    ...liveQueryOptions,
  });

export const useCameraFocus = (deviceId: string | null) =>
  useQuery({
    queryKey: controlKey(deviceId ?? "", "focus"),
    queryFn: async () => {
      const { data } = await axiosInstance.get<{ focus: FocusState }>(
        `/api/camera/${deviceId}/focus`,
      );
      return data.focus;
    },
    enabled: !!deviceId,
    ...liveQueryOptions,
  });

// ---------------------------------------------------------------------------
// Mutations (apply settings, capture photo, stream control)
//
// The camera applies settings asynchronously (its ISP needs a few frames), so
// the live values the backend reads right after apply - and returns in the
// PUT response - can still be the OLD ones. The cache is therefore updated
// with the values the user commanded, and a delayed refetch confirms the
// real hardware state once the camera has settled.
// ---------------------------------------------------------------------------

const CONFIRM_DELAY_MS = 2000;

const confirmFromHardwareLater = (
  queryClient: QueryClient,
  deviceId: string,
  part: string,
) => {
  setTimeout(() => {
    void queryClient.invalidateQueries({
      queryKey: controlKey(deviceId, part),
    });
  }, CONFIRM_DELAY_MS);
};

export interface SetExposureInput {
  mode: "auto" | "manual";
  analogue_gain?: number;
  exposure_time_us?: number;
}

export const useSetExposure = (deviceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetExposureInput) => {
      const { data } = await axiosInstance.put<{
        exposure: ExposureState;
        applied_settings?: AppliedSettings;
      }>(`/api/camera/${deviceId}/exposure`, input);
      return data;
    },
    onSuccess: (data, input) => {
      queryClient.setQueryData<ExposureState | undefined>(
        controlKey(deviceId, "exposure"),
        (previous) => ({
          analogue_gain: null,
          exposure_time: null,
          ...previous,
          ae_mode: input.mode,
          ...(input.mode === "manual"
            ? {
                analogue_gain:
                  data.applied_settings?.analogue_gain ??
                  input.analogue_gain ??
                  null,
                exposure_time:
                  data.applied_settings?.exposure_time ??
                  input.exposure_time_us ??
                  null,
              }
            : {}),
        }),
      );
      confirmFromHardwareLater(queryClient, deviceId, "exposure");
    },
  });
};

export interface SetWhiteBalanceInput {
  mode: "auto" | "manual";
  red_gain?: number;
  blue_gain?: number;
}

export const useSetWhiteBalance = (deviceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetWhiteBalanceInput) => {
      const { data } = await axiosInstance.put<{
        white_balance: WhiteBalanceState;
        applied_settings?: AppliedSettings;
      }>(`/api/camera/${deviceId}/white_balance`, input);
      return data;
    },
    onSuccess: (data, input) => {
      queryClient.setQueryData<WhiteBalanceState | undefined>(
        controlKey(deviceId, "white_balance"),
        (previous) => ({
          awb_red_gain: null,
          awb_blue_gain: null,
          ...previous,
          awb_mode: input.mode,
          ...(input.mode === "manual"
            ? {
                awb_red_gain:
                  data.applied_settings?.awb_red_gain ??
                  input.red_gain ??
                  null,
                awb_blue_gain:
                  data.applied_settings?.awb_blue_gain ??
                  input.blue_gain ??
                  null,
              }
            : {}),
        }),
      );
      confirmFromHardwareLater(queryClient, deviceId, "white_balance");
    },
  });
};

export interface SetFocusInput {
  mode: "auto" | "continuous" | "manual";
  lens_position?: number;
  roi?: Roi;
}

export const useSetFocus = (deviceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetFocusInput) => {
      // The auto mode runs a full AF scan + lock on the camera
      const { data } = await axiosInstance.put<SetFocusResponse>(
        `/api/camera/${deviceId}/focus`,
        input,
        { timeout: 60_000 },
      );
      return data;
    },
    onSuccess: (data) => {
      const commanded = data.applied_settings?.focus_settings;
      queryClient.setQueryData<FocusState | undefined>(
        controlKey(deviceId, "focus"),
        (previous) => {
          const base = previous ?? data.focus;
          if (!base) return data.focus;
          return {
            ...base,
            af_mode: commanded?.af_mode ?? base.af_mode,
            lens_position:
              commanded?.lens_position ??
              (data.autofocus?.locked
                ? data.autofocus.lens_position
                : base.lens_position),
            af_state: data.autofocus?.af_state ?? base.af_state,
            af_state_name: data.autofocus?.af_state_name ?? base.af_state_name,
          };
        },
      );
      confirmFromHardwareLater(queryClient, deviceId, "focus");
    },
  });
};

export interface ServicePhoto {
  url: string; // object URL of the JPEG blob
  size: number;
  takenAt: Date;
  source: string | null; // "upload" | "s3"
}

export const useTakePhoto = (deviceId: string) =>
  useMutation({
    mutationFn: async (quality: number): Promise<ServicePhoto> => {
      const response = await axiosInstance.get(
        `/api/camera/${deviceId}/photo`,
        {
          params: { quality, timeout_s: 45 },
          responseType: "blob",
          timeout: 90_000,
        },
      );
      const blob = response.data as Blob;
      return {
        url: URL.createObjectURL(blob),
        size: blob.size,
        takenAt: new Date(),
        source: response.headers["x-photo-source"] ?? null,
      };
    },
  });

export interface StartStreamInput {
  endpoint: string;
  quality?: number;
}

export const useStartStream = (deviceId: string) =>
  useMutation({
    mutationFn: async (input: StartStreamInput) => {
      const { data } = await axiosInstance.post<StreamStartResponse>(
        `/api/camera/${deviceId}/stream/start`,
        input,
      );
      return data;
    },
  });

export const useStopStream = (deviceId: string) =>
  useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.post<{ success: boolean }>(
        `/api/camera/${deviceId}/stream/stop`,
        {},
      );
      return data;
    },
  });
